import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsSales } from '@/api/goodsSales';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { amountToWordsUA } from '@/utils/numberToWordsUA';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function formatDateUk(iso: string): string {
  const d = new Date(iso);
  return `« ${d.getDate()} » ${MONTHS_UK[d.getMonth()]!} ${d.getFullYear()} р.`;
}

export default function GoodsSalePrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['goods-sale', id],
    queryFn: () => goodsSales.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 1000 }),
  });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', docQ.data?.organization_id],
    queryFn: () => refs.warehouses(docQ.data!.organization_id),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const nomMap = useMemo(() => {
    const m = new Map<string, { code: string | null; name: string; is_service: boolean }>();
    nomQ.data?.items.forEach((n) => m.set(n.id, {
      code: n.code, name: n.name, is_service: n.is_service,
    }));
    return m;
  }, [nomQ.data]);

  if (!docQ.data || !orgsQ.data || !contractorsQ.data || !nomQ.data) {
    return <div className="card"><p>Завантаження…</p></div>;
  }

  const d = docQ.data;
  const org = orgsQ.data.items.find((o) => o.id === d.organization_id);
  const buyer = contractorsQ.data.items.find((c) => c.id === d.contractor_id);
  const wh = warehousesQ.data?.find((w) => w.id === d.warehouse_id);

  if (!org || !buyer) {
    return <div className="card"><p>Не знайдено організацію або покупця.</p></div>;
  }

  const itemsCount = d.items.length;

  // Тип документа определяется составом строк:
  // - все услуги → Акт виконаних робіт
  // - всё остальное (только товары / смешанный) → Видаткова накладна
  const allServices = d.items.length > 0 && d.items.every(
    (it) => nomMap.get(it.nomenclature_id)?.is_service === true,
  );
  const docTitle = allServices
    ? 'Акт виконаних робіт (наданих послуг)'
    : 'Видаткова накладна';
  const itemColumnHeader = allServices
    ? 'Найменування робіт (послуг)'
    : 'Найменування товару (послуги)';
  const supplierSignLabel = allServices ? 'Виконавець (підпис, П.І.Б.)' : 'Відпустив (підпис, П.І.Б.)';
  const buyerSignLabel = allServices ? 'Замовник (підпис, П.І.Б.)' : 'Отримав (підпис, П.І.Б.)';

  return (
    <div className="print-page invoice-print">
      <style>{`
        .invoice-print {
          background: white; color: black; max-width: 900px; margin: 0 auto;
          padding: 1.5rem;
          font-family: 'Times New Roman', serif; font-size: 10.5pt; line-height: 1.3;
        }
        .invoice-print h1 { font-size: 13pt; text-align: center; margin: 0; }
        .invoice-print .center { text-align: center; }
        .invoice-print .right { text-align: right; }
        .invoice-print .small { font-size: 9pt; color: #555; }
        .invoice-print .header { margin-bottom: 0.75rem; }
        .invoice-print .party { margin: 0.5rem 0; }
        .invoice-print .party .label { font-size: 9pt; color: #555; }
        .invoice-print table { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-top: 0.75rem; }
        .invoice-print th, .invoice-print td { border: 1px solid black; padding: 4px 6px; vertical-align: top; }
        .invoice-print th { background: #f5f5f5; font-weight: 600; text-align: center; }
        .invoice-print td.num { text-align: right; font-variant-numeric: tabular-nums; }
        .invoice-print .totals-line { margin-top: 0.5rem; }
        .invoice-print .totals-line strong { font-weight: 600; }
        .invoice-print .signs { margin-top: 1.5rem; display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; }
        .invoice-print .sign { border-bottom: 1px solid black; min-height: 22px; }
        .invoice-print .sign-label { font-size: 9pt; color: #555; margin-top: 4px; }
        .invoice-print .actions { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
        @media print {
          .invoice-print .actions { display: none; }
          .invoice-print { padding: 0; max-width: none; }
          body { background: white !important; }
        }
      `}</style>

      <div className="actions print-hide">
        <button onClick={() => window.print()} className="primary">🖨 Друк / PDF</button>
        <button onClick={() => window.history.back()}>← Назад</button>
      </div>

      <div className="header">
        <h1>{docTitle} № {d.document_number}</h1>
        <p className="center" style={{ margin: '0.25rem 0' }}>від {formatDateUk(d.document_date)}</p>
      </div>

      <div className="party">
        <span className="label">Постачальник: </span>
        <strong>{org.full_name || org.name}</strong>
        {org.edrpou && <span>, ЄДРПОУ {org.edrpou}</span>}
        {org.vat_number && <span>, ІПН {org.vat_number}</span>}
      </div>

      <div className="party">
        <span className="label">Одержувач: </span>
        <strong>{buyer.full_name || buyer.name}</strong>
        {buyer.edrpou && <span>, ЄДРПОУ {buyer.edrpou}</span>}
        {buyer.vat_number && <span>, ІПН {buyer.vat_number}</span>}
      </div>

      {wh && !allServices && (
        <div className="party">
          <span className="label">Склад відвантаження: </span>
          {wh.code} — {wh.name}
        </div>
      )}

      {d.delivery_address && (
        <div className="party">
          <span className="label">Адреса доставки: </span>
          {d.delivery_address}
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>{itemColumnHeader}</th>
            <th style={{ width: 40 }}>Од.</th>
            <th style={{ width: 70 }}>К-сть</th>
            <th style={{ width: 90 }}>Ціна без ПДВ</th>
            <th style={{ width: 100 }}>Сума без ПДВ</th>
            <th style={{ width: 80 }}>Сума ПДВ</th>
            <th style={{ width: 100 }}>Усього з ПДВ</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it) => {
            const nom = nomMap.get(it.nomenclature_id);
            const unit = nom?.is_service ? 'посл.' : 'шт.';
            return (
              <tr key={it.id ?? it.line_number}>
                <td className="center">{it.line_number}</td>
                <td>
                  {nom?.name ?? '—'}
                  {nom?.code && <span className="small"> ({nom.code})</span>}
                </td>
                <td className="center">{unit}</td>
                <td className="num">{fmtQty(it.quantity)}</td>
                <td className="num">{fmt2(it.price)}</td>
                <td className="num">{fmt2(it.amount_without_vat)}</td>
                <td className="num">{fmt2(it.vat_amount)}</td>
                <td className="num">{fmt2(it.total_amount)}</td>
              </tr>
            );
          })}
          <tr style={{ fontWeight: 600 }}>
            <td colSpan={5} className="right">Усього:</td>
            <td className="num">{fmt2(d.amount_without_vat)}</td>
            <td className="num">{fmt2(d.vat_amount)}</td>
            <td className="num">{fmt2(d.total_amount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="totals-line" style={{ marginTop: '0.75rem' }}>
        <strong>Кількість найменувань:</strong> {itemsCount}
      </div>
      <div className="totals-line">
        <strong>Усього до сплати з ПДВ:</strong> <em>{amountToWordsUA(d.total_amount)}</em>
      </div>
      <div className="totals-line">
        <strong>У т.ч. ПДВ:</strong> {fmt2(d.vat_amount)} грн
      </div>

      <div className="signs">
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">{supplierSignLabel}</div>
        </div>
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">{buyerSignLabel}</div>
        </div>
      </div>

      <p className="small" style={{ marginTop: '1rem' }}>
        М.П. Постачальника &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        М.П. Одержувача
      </p>
    </div>
  );
}
